/**
 * Recording edit-operation builders — the single source of truth for turning
 * form state into the op payloads of contracts 4.1.1, 4.1.3, 4.2.1 and 4.4.2.
 *
 * Extracted from `RecordingEditForm`'s `handleSaveRecordingDetails` so that the
 * create flow (WMC-515) can emit the same ops from the same code rather than a
 * parallel copy that drifts. Keep this module pure (no React / JSX imports),
 * like its sibling ./recordingValidators — every surface should be free to
 * import it.
 *
 * ## The `(current, original)` signature
 *
 * Every builder takes the current value first and the baseline it is being
 * diffed against second, and returns the ops needed to get from one to the
 * other. Create mode is not a mode here — it is the case where the baseline is
 * empty, so `EMPTY_BASELINE` (or a per-field empty: `''`, `[]`) is passed and
 * the diff naturally comes out as all-adds. That is deliberately *not* a
 * discriminated union: a `{ mode: 'create' } | { mode: 'edit', original }`
 * parameter would force every builder to branch, and every branch is somewhere
 * the two modes can drift apart.
 *
 * A consequence worth stating: these builders subsume the form's
 * `isFieldModified` gate. A field whose current value equals its baseline emits
 * no ops, so callers do not need to check first. This also closes a class of
 * bug the old gate allowed — the gate and the payload normalised independently
 * (trimming, `liveToString`), so they could in principle disagree about whether
 * a field had changed. Here each builder normalises once and both the
 * comparison and the emitted value use that result.
 */

import {
  ChangeAssociatedArtistsRequestEditsInnerFeaturedStatusEnum,
  ChangeAssociatedArtistsRequestEditsInnerOpEnum,
  CorrectRecordingRequestEditsInnerOpEnum,
  CreateArtistAndLinkRequestEditsInnerFeaturedStatusEnum,
  CreateArtistAndLinkRequestEditsInnerOpEnum,
  CreateArtistAndLinkRequestEditsInnerSourceEnum,
  ManageContributionsRequestEditsInnerOpEnum,
} from 'shared_components/generated/admin';
import type {
  ChangeAssociatedArtistsRequestEditsInner,
  CorrectRecordingRequestEditsInner,
  CreateArtistAndLinkRequestEditsInner,
  ManageContributionsRequestEditsInner,
} from 'shared_components/generated/admin';
import { liveToString } from './recordingValidators';

// ── Inputs ───────────────────────────────────────────────────────────────────

/**
 * The artist shape the builders need, declared structurally rather than
 * imported from `./Components/ArtistEditorModal`.
 *
 * That module is a `.tsx` component; importing its type would tie this pure
 * module to the component tree and drag JSX into the fast, DOM-less jest
 * project. `ArtistEntry` is assignable to this, so the form passes its state
 * through unchanged — every field is optional here because the builders already
 * handle absence, which is exactly the create-mode case.
 */
export interface ArtistMemberLike {
  id: string;
  stageName: string;
  /** True when the member already exists in the WMC graph and `id` is the
   *  internal WMC id; false when `id` is the source-side id. */
  exists?: boolean;
}

export interface ArtistEntryLike {
  name: string;
  id?: string;
  isFeatured?: boolean;
  featuredStatus?: 'fa' | 'ofa' | 'nf';
  members?: ArtistMemberLike[];
  type?: 'persona' | 'group';
  /** When set, this artist does not exist yet — it goes through `/create`
   *  (contract 4.4.2) instead of the 4.1.3 link payload. */
  pendingCreate?: { source: 'discogs' | 'musicbrainz'; sourceId: string };
  demoted?: boolean;
}

/** The scalar recording fields, as the form holds them. */
export interface RecordingFormValues {
  title?: string | null;
  duration?: string | null;
  /** Live descriptor. `unknown` because the form's `live` is loosely typed;
   *  `liveToString` narrows it. */
  live?: unknown;
  yearRecorded?: string | null;
  location?: string | null;
}

/** A contribution as the form holds it. The generated model has no `personaId`
 *  — it rides along on the row — so it is declared here rather than reached for
 *  with `as any` at each use site. */
export interface ContributionEntry {
  contribution: string;
  confidenceCategory?: string;
  confidencePattern?: string;
  personaId?: string;
}

/** Every field the recording save diffs, in one bag. Used for both sides of the
 *  diff: the current form state and the baseline it is compared against. */
export interface RecordingValues {
  formData: RecordingFormValues;
  versions: string[];
  isrcs: string[];
  pNames: string[];
  artists: ArtistEntryLike[];
  contributions: ContributionEntry[];
}

/** Where a duplicate ISRC is being reassigned from. */
export interface DuplicateIsrcSource {
  recordingId: string;
  documentId: string;
}

/**
 * The baseline for a recording that does not exist yet.
 *
 * Frozen because create mode passes this same object on every save; a builder
 * that mutated its baseline would silently poison every later create in the
 * session.
 */
export const EMPTY_BASELINE: Readonly<RecordingValues> = Object.freeze({
  formData: Object.freeze({}) as RecordingFormValues,
  versions: Object.freeze([]) as unknown as string[],
  isrcs: Object.freeze([]) as unknown as string[],
  pNames: Object.freeze([]) as unknown as string[],
  artists: Object.freeze([]) as unknown as ArtistEntryLike[],
  contributions: Object.freeze([]) as unknown as ContributionEntry[],
});

// ── Scalar fields (contract 4.1.1) ───────────────────────────────────────────

const editOp = (
  field: string,
  value: string
): CorrectRecordingRequestEditsInner => ({
  op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
  field,
  value: value as unknown as object,
});

/** Recording title. Sent raw — the form has already rejected a blank one. */
export const buildTitleEdits = (
  current: string | null | undefined,
  original: string | null | undefined
): CorrectRecordingRequestEditsInner[] =>
  current === original ? [] : [editOp('recordingTitle', current ?? '')];

/** Duration, as HH:MM:SS. */
export const buildDurationEdits = (
  current: string | null | undefined,
  original: string | null | undefined
): CorrectRecordingRequestEditsInner[] =>
  current === original ? [] : [editOp('duration', current ?? '')];

/**
 * Live descriptor.
 *
 * Only the `live` descriptor string is persisted. The former boolean `is_live`
 * flag is no longer written — it is derived downstream from the presence of a
 * descriptor. The descriptor is a standalone field, never mirrored into
 * recordingVersion. (OS schema: ParentRecording.live, e.g. "Live in London,
 * 1989".)
 */
export const buildLiveEdits = (
  current: unknown,
  original: unknown
): CorrectRecordingRequestEditsInner[] => {
  const value = liveToString(current);
  return value === liveToString(original) ? [] : [editOp('live', value)];
};

/** Year of Recording. Trimmed; an emptied field sends '' so the backend clears
 *  it. Only sent when changed, so an untouched empty field never emits a
 *  clear. */
export const buildYearRecordedEdits = (
  current: string | null | undefined,
  original: string | null | undefined
): CorrectRecordingRequestEditsInner[] => {
  const value = (current ?? '').trim();
  return value === (original ?? '').trim()
    ? []
    : [editOp('yearRecorded', value)];
};

/** Country of Recording. Same clear-on-empty rule as the year. */
export const buildCountryEdits = (
  current: string | null | undefined,
  original: string | null | undefined
): CorrectRecordingRequestEditsInner[] => {
  const value = current ?? '';
  return value === (original ?? '') ? [] : [editOp('countryOfRecording', value)];
};

// ── List fields (contract 4.1.1) ─────────────────────────────────────────────

/**
 * The shared shape of the list fields: one add per new entry, one delete per
 * dropped one. `deletesFirst` preserves each field's existing emission order —
 * see the note on `buildIsrcEdits`.
 */
const buildListEdits = (
  field: string,
  current: string[],
  original: string[],
  deletesFirst: boolean
): CorrectRecordingRequestEditsInner[] => {
  const added = current.filter((v) => !original.includes(v));
  const removed = original.filter((v) => !current.includes(v));
  const adds = added.map((v) => ({
    op: CorrectRecordingRequestEditsInnerOpEnum.Add,
    field,
    value: v as unknown as object,
  }));
  const deletes = removed.map((v) => ({
    op: CorrectRecordingRequestEditsInnerOpEnum.Delete,
    field,
    value: v as unknown as object,
  }));
  return deletesFirst ? [...deletes, ...adds] : [...adds, ...deletes];
};

/** recordingVersion is list[str] in the OS schema: delete removed, add new. */
export const buildVersionEdits = (
  current: string[],
  original: string[]
): CorrectRecordingRequestEditsInner[] =>
  buildListEdits('recordingVersion', current, original, true);

/** pNames is list[str] in the OS schema: one add/delete per name. */
export const buildPNameEdits = (
  current: string[],
  original: string[]
): CorrectRecordingRequestEditsInner[] =>
  buildListEdits('pNames', current, original, true);

/**
 * ISRCs, with the reassignment riders.
 *
 * Adds come before deletes here, which is the order the form has always
 * emitted. It matters: an ISRC being *moved* between recordings is an add on
 * this recording carrying `dupIsrcs`, and the backend reads that rider to strip
 * the code from the recordings named in it. Emitting the delete first would
 * describe a different intent.
 */
export const buildIsrcEdits = (
  current: string[],
  original: string[],
  confirmedDuplicates: Record<string, DuplicateIsrcSource[]> = {}
): CorrectRecordingRequestEditsInner[] => {
  const edits = buildListEdits('isrcs', current, original, false);
  return edits.map((edit) => {
    if (edit.op !== CorrectRecordingRequestEditsInnerOpEnum.Add) return edit;
    const matches = confirmedDuplicates[edit.value as unknown as string];
    if (!matches || matches.length === 0) return edit;
    return {
      ...edit,
      dupIsrcs: matches.map((d) => ({
        dupRecordingId: d.recordingId,
        dupDocumentId: d.documentId,
      })),
    };
  });
};

/** Every scalar and list edit for contract 4.1.1, in the form's emission
 *  order. */
export const buildRecordingEdits = (
  current: RecordingValues,
  original: Readonly<RecordingValues>,
  confirmedDuplicates: Record<string, DuplicateIsrcSource[]> = {}
): CorrectRecordingRequestEditsInner[] => [
  ...buildTitleEdits(current.formData.title, original.formData.title),
  ...buildDurationEdits(current.formData.duration, original.formData.duration),
  ...buildLiveEdits(current.formData.live, original.formData.live),
  ...buildVersionEdits(current.versions, original.versions),
  ...buildIsrcEdits(current.isrcs, original.isrcs, confirmedDuplicates),
  ...buildYearRecordedEdits(
    current.formData.yearRecorded,
    original.formData.yearRecorded
  ),
  ...buildCountryEdits(current.formData.location, original.formData.location),
  ...buildPNameEdits(current.pNames, original.pNames),
];

// ── Artists (contracts 4.4.2 and 4.1.3) ──────────────────────────────────────

/**
 * Was this artist ever persisted against the recording?
 *
 * This is the predicate the artist ops are supposed to key off, and it replaces
 * the `pendingCreate` flag they used to use. The two agree in edit mode and
 * disagree in create mode, where *nothing* has been persisted and yet only some
 * artists carry `pendingCreate` — a plain existing artist picked from lookup has
 * no such flag, so the old test would have called it persisted and emitted a
 * delete against an edge that was never written.
 *
 * Matched on **name**, not id, because that is what every artist op around it
 * compares — an artist added by lookup has no id until the server assigns one,
 * so name is the only identity available on both sides of the diff. Matching on
 * id instead would change edit-mode behaviour: an artist whose name changed
 * while keeping its id would stop emitting its add and emit only a delete.
 * Renaming is not an operation this form offers (you remove and re-add), so the
 * two only diverge on input the UI cannot produce — but the incumbent semantics
 * are the ones to preserve here.
 */
export const wasArtistPersisted = (
  artist: Pick<ArtistEntryLike, 'name'>,
  original: readonly ArtistEntryLike[]
): boolean => original.some((o) => o.name === artist.name);

/**
 * New artists that must be created before they can be linked (contract 4.4.2).
 *
 * These are excluded from the 4.1.3 payload below — `/create` both creates and
 * links them in one batched call.
 */
export const buildCreateArtistEdits = (
  current: ArtistEntryLike[],
  original: Readonly<RecordingValues>['artists']
): CreateArtistAndLinkRequestEditsInner[] =>
  current
    .filter((a) => a.pendingCreate && !wasArtistPersisted(a, original))
    .map((a) => ({
      op: CreateArtistAndLinkRequestEditsInnerOpEnum.Add,
      stageName: a.name,
      source: a.pendingCreate!
        .source as CreateArtistAndLinkRequestEditsInnerSourceEnum,
      sourceId: a.pendingCreate!.sourceId,
      featuredStatus:
        a.featuredStatus === 'fa'
          ? CreateArtistAndLinkRequestEditsInnerFeaturedStatusEnum.Fa
          : CreateArtistAndLinkRequestEditsInnerFeaturedStatusEnum.Ofa,
      members:
        a.members && a.members.length > 0
          ? a.members.map((m) => ({
              id: m.id,
              stageName: m.stageName,
              _exists: m.exists ?? false,
            }))
          : undefined,
    }));

/**
 * Link, unlink and demote existing artists (contract 4.1.3).
 *
 * Three cases, in the form's emission order:
 *  - **added** — in the list, not in the baseline, and not going through
 *    `/create`;
 *  - **removed** — in the baseline, gone from the list, and not demoted;
 *  - **demoted** — kept as a tombstone and sent with `nfa`, which is what
 *    distinguishes "no longer featured" from "no longer on the recording".
 *
 * A demoted artist that was never persisted emits nothing: there is no edge to
 * demote. In edit mode that case cannot arise, so this is create mode's rule,
 * enforced by the same predicate rather than a mode check.
 */
export const buildArtistEdits = (
  current: ArtistEntryLike[],
  original: Readonly<RecordingValues>['artists'],
  demoted: ArtistEntryLike[] = []
): ChangeAssociatedArtistsRequestEditsInner[] => {
  const edits: ChangeAssociatedArtistsRequestEditsInner[] = [];
  const currentNames = current.map((a) => a.name);
  const demotedNames = demoted.map((d) => d.name);

  current
    .filter((a) => !wasArtistPersisted(a, original) && !a.pendingCreate)
    .forEach((a) => {
      edits.push({
        artistId: a.id || a.name,
        op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Add,
        featuredStatus: (a.featuredStatus ??
          (a.isFeatured ? 'fa' : 'ofa')) as 'fa' | 'ofa' | 'nfa',
        stageName: a.name,
        // A linked artist's lineup was read off the graph, so a member with
        // no flag is one that exists. Same default as `bulkEditBatch`.
        members: (a.members ?? []).map((m) => ({
          id: m.id,
          stageName: m.stageName,
          _exists: m.exists ?? true,
        })),
      });
    });

  original
    .filter(
      (a) => !currentNames.includes(a.name) && !demotedNames.includes(a.name)
    )
    .forEach((a) => {
      edits.push({
        artistId: a.id || a.name,
        op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Delete,
        // @ts-expect-error -- contract allows null for remove
        featuredStatus: null,
        type: a.type,
      });
    });

  demoted
    .filter((a) => wasArtistPersisted(a, original))
    .forEach((a) => {
      edits.push({
        artistId: a.id || a.name,
        op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Delete,
        featuredStatus:
          ChangeAssociatedArtistsRequestEditsInnerFeaturedStatusEnum.Nfa,
        type: a.type,
      });
    });

  return edits;
};

// ── Contributions (contract 4.2.1) ───────────────────────────────────────────

/** `confidencePattern` doubles as a JSON blob carrying the evidence URL and
 *  notes. Anything unparseable is treated as absent rather than thrown. */
export const parseContributionEvidenceNotes = (
  confidencePattern: string
): { evidenceUrl: string; notes: string } => {
  if (!confidencePattern?.trim()) return { evidenceUrl: '', notes: '' };
  try {
    const o = JSON.parse(confidencePattern) as unknown;
    if (typeof o === 'object' && o && !Array.isArray(o)) {
      const obj = o as Record<string, unknown>;
      return {
        evidenceUrl: typeof obj.evidenceUrl === 'string' ? obj.evidenceUrl : '',
        notes: typeof obj.notes === 'string' ? obj.notes : '',
      };
    }
  } catch {
    // A pattern that isn't JSON is a legacy free-text one; it carries neither.
  }
  return { evidenceUrl: '', notes: '' };
};

const contributionPayload = (
  op: ManageContributionsRequestEditsInnerOpEnum,
  c: ContributionEntry
): ManageContributionsRequestEditsInner => {
  const { evidenceUrl, notes } = parseContributionEvidenceNotes(
    c.confidencePattern ?? ''
  );
  return {
    op,
    role: c.contribution ?? '',
    personaId: c.personaId || undefined,
    dataCategory: c.confidenceCategory ?? '1',
    sourceUrl: evidenceUrl || undefined,
    notes: notes || undefined,
  };
};

/** True when the same role carries different detail on the two sides. Role is
 *  the identity, so a changed role is an add plus a delete, not an edit. */
const contributionChanged = (
  current: ContributionEntry,
  original: ContributionEntry
): boolean =>
  original.confidenceCategory !== current.confidenceCategory ||
  original.confidencePattern !== current.confidencePattern ||
  original.personaId !== current.personaId;

/** Add, remove and edit contributions, in the form's emission order. */
export const buildContributionEdits = (
  current: ContributionEntry[],
  original: Readonly<RecordingValues>['contributions']
): ManageContributionsRequestEditsInner[] => {
  const edits: ManageContributionsRequestEditsInner[] = [];

  current
    .filter((c) => !original.some((oc) => oc.contribution === c.contribution))
    .forEach((c) =>
      edits.push(
        contributionPayload(ManageContributionsRequestEditsInnerOpEnum.Add, c)
      )
    );

  original
    .filter((oc) => !current.some((c) => c.contribution === oc.contribution))
    .forEach((oc) =>
      edits.push({
        op: ManageContributionsRequestEditsInnerOpEnum.Delete,
        role: oc.contribution ?? '',
      })
    );

  current
    .filter((c) => {
      const orig = original.find((oc) => oc.contribution === c.contribution);
      return !!orig && contributionChanged(c, orig);
    })
    .forEach((c) =>
      edits.push(
        contributionPayload(ManageContributionsRequestEditsInnerOpEnum.Edit, c)
      )
    );

  return edits;
};
